import React, { useEffect, useRef, useState } from "react";
import { Input, Spin } from "antd";
import { checkDashboardDuplicate } from "../../api/dashboardApi";
import "./BoardNameInput.css";

/**
 * Is a dashboard/report name free? Asked while the user types, after a pause.
 *
 *   status: "idle"      nothing to check (blank, or unchanged from `originalName`)
 *           "checking"  request pending — callers must not save yet
 *           "available" free
 *           "duplicate" taken — `message` says so
 *           "error"     the check itself failed — not treated as taken
 *
 * `boardId` is the board being renamed; the API excludes it, so keeping its own
 * name is never a clash. `originalName` short-cuts that case without a request.
 * Answers that arrive after the name has changed again are dropped.
 */
export function useBoardNameCheck(name, { boardId, originalName, onlyWhenEdited = false } = {}) {
  const [result, setResult] = useState({ status: "idle", message: "" });
  const seq = useRef(0);

  /* onlyWhenEdited (the editor): the first name the box is given is the board's
     own, arriving when the board loads — not something the user typed. It is
     taken as the baseline and never checked; only a name typed differently is.
     So opening a board makes no request, and an old duplicate it was saved
     under before this check existed is not flagged until it is renamed. Reset
     per board, in case the editor moves to another one. */
  const baseline = useRef(null);
  const baselineFor = useRef(boardId);
  if (baselineFor.current !== boardId) {
    baselineFor.current = boardId;
    baseline.current = null;
  }
  if (onlyWhenEdited && baseline.current === null && String(name || "").trim()) {
    baseline.current = String(name).trim().toLowerCase();
  }

  useEffect(() => {
    const trimmed = String(name || "").trim();
    const mine = (seq.current += 1);

    const unchanged =
      (boardId && originalName && trimmed.toLowerCase() === String(originalName).trim().toLowerCase()) ||
      (onlyWhenEdited && trimmed.toLowerCase() === baseline.current);
    if (!trimmed || unchanged) {
      setResult({ status: "idle", message: "" });
      return undefined;
    }

    setResult({ status: "checking", message: "" });
    const timer = setTimeout(async () => {
      try {
        const { isDuplicate, message } = await checkDashboardDuplicate(trimmed, boardId);
        if (mine !== seq.current) return;
        setResult(
          isDuplicate
            ? { status: "duplicate", message: message || "A dashboard or report with this name already exists" }
            : { status: "available", message: "" },
        );
      } catch (e) {
        if (mine !== seq.current) return;
        setResult({ status: "error", message: "" });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [name, boardId, originalName, onlyWhenEdited]);

  return result;
}

/* Saving has to wait while the check is out, and stop on a taken name. A failed
   check does not block — the user should not be stuck because the check is down. */
export const blocksSave = (status) => status === "checking" || status === "duplicate";

/**
 * The name box for a dashboard/report — the Create page and the editor's Name
 * field use the same one, so both check the same way.
 *
 * Reports its status up through `onStatusChange` (the parent decides what to
 * block). The duplicate message is shown under the box unless `showMessage` is
 * false — the editor's toolbar has no room, so it shows the mark and a tooltip.
 */
export default function BoardNameInput({
  value,
  onChange,
  onBlur,
  boardId,
  originalName,
  // Only check once the user has typed a different name (see useBoardNameCheck).
  onlyWhenEdited = false,
  onStatusChange,
  className = "",
  placeholder,
  showMessage = true,
  messageClassName = "board-name-message",
  /* Optional box around the input and its message — lets a page position the
     message against the input itself (the editor hangs it underneath). */
  wrapperClassName,
}) {
  const { status, message } = useBoardNameCheck(value, { boardId, originalName, onlyWhenEdited });

  useEffect(() => {
    if (onStatusChange) onStatusChange(status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const isDuplicate = status === "duplicate";
  /* Always rendered: antd swaps the bare input for a wrapper when a suffix
     appears, which re-mounts it and throws the cursor out mid-word. */
  const mark =
    status === "checking" ? (
      <Spin size="small" />
    ) : status === "available" ? (
      <span className="board-name-mark board-name-mark--ok" aria-label="Name is available">
        ✓
      </span>
    ) : isDuplicate ? (
      <span className="board-name-mark board-name-mark--taken" title={message} aria-label="Name already exists">
        ⚠
      </span>
    ) : null;

  const Wrap = wrapperClassName ? "span" : React.Fragment;
  const wrapProps = wrapperClassName ? { className: wrapperClassName } : {};

  return (
    <Wrap {...wrapProps}>
      <Input
        className={`${className}${isDuplicate ? " has-error board-name-taken" : ""}`}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        title={isDuplicate ? message : undefined}
        suffix={<span className="board-name-suffix">{mark}</span>}
      />
      {showMessage && isDuplicate ? <div className={messageClassName}>{message}</div> : null}
    </Wrap>
  );
}
